import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { delftBlue, flame } from "@/theme/colors";

const entityId = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  return String(item.id || item._id || "");
};

const ActivityCompanyAssignments = ({
  companyOptions = [],
  selectedIds = [],
  onChange,
  assignments = [],
  versions = [],
  newCompanyVersions = {},
  onNewCompanyVersionsChange,
  hasTemplate = false,
  defaultFormVersion = "",
  error,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [companyToAdd, setCompanyToAdd] = useState("");
  const [versionToAdd, setVersionToAdd] = useState(defaultFormVersion);
  const [dialogError, setDialogError] = useState("");

  const companyNameById = useMemo(() => {
    const map = {};
    companyOptions.forEach((company) => {
      map[company.code] = company.name;
    });
    return map;
  }, [companyOptions]);

  const versionLabelByNumber = useMemo(() => {
    const map = {};
    versions.forEach((row) => {
      map[String(row.version)] = row.versionName
        ? `v${row.version} — ${row.versionName}`
        : `v${row.version}`;
    });
    return map;
  }, [versions]);

  const publishedVersionByCompany = useMemo(() => {
    const map = {};
    assignments.forEach((row) => {
      const id = entityId(row.companyId);
      if (id) {
        map[id] = String(row.publishedVersion);
      }
    });
    return map;
  }, [assignments]);

  const availableToAdd = companyOptions.filter((company) => !selectedIds.includes(company.code));

  const openDialog = () => {
    setCompanyToAdd(availableToAdd[0]?.code || "");
    setVersionToAdd(defaultFormVersion);
    setDialogError("");
    setDialogOpen(true);
  };

  const handleAdd = () => {
    if (!companyToAdd) {
      setDialogError("Select a company");
      return;
    }
    if (hasTemplate && !versionToAdd) {
      setDialogError("Select a form version for this company");
      return;
    }
    onChange([...selectedIds, companyToAdd]);
    if (hasTemplate) {
      onNewCompanyVersionsChange({
        ...newCompanyVersions,
        [companyToAdd]: String(versionToAdd),
      });
    }
    setDialogOpen(false);
  };

  const handleRemove = (companyId) => {
    const name = companyNameById[companyId] || "this company";
    if (
      !window.confirm(
        `Remove ${name} from this activity? They will no longer be assigned.`
      )
    ) {
      return;
    }
    onChange(selectedIds.filter((id) => id !== companyId));
    if (newCompanyVersions[companyId]) {
      const next = { ...newCompanyVersions };
      delete next[companyId];
      onNewCompanyVersionsChange(next);
    }
  };

  const versionChip = (companyId) => {
    if (newCompanyVersions[companyId]) {
      return {
        label: `${versionLabelByNumber[newCompanyVersions[companyId]] || `v${newCompanyVersions[companyId]}`} · new`,
        color: flame[500],
      };
    }
    if (publishedVersionByCompany[companyId]) {
      const version = publishedVersionByCompany[companyId];
      return {
        label: versionLabelByNumber[version] || `v${version}`,
        color: delftBlue[500],
      };
    }
    if (hasTemplate) {
      return { label: "No version yet", color: delftBlue[400] };
    }
    return { label: "Activity only", color: delftBlue[400] };
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        outline: `1px ${delftBlue[100]} solid`,
        padding: 2,
        background: "white",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <Box>
            <Typography
              sx={{
                color: delftBlue[500],
                fontFamily: "Audiowide, sans-serif",
                fontSize: 16,
              }}
            >
              Companies
            </Typography>
            <Typography
              sx={{
                color: delftBlue[400],
                fontFamily: "Montserrat, sans-serif",
                fontSize: 13,
              }}
            >
              {hasTemplate
                ? "Each company uses a specific form version. New companies pick a version when you add them."
                : "Assign which companies can run this activity."}
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={openDialog}
            disabled={!availableToAdd.length}
            sx={{
              textTransform: "none",
              fontFamily: "Montserrat, sans-serif",
              background: flame[500],
              color: "white",
              "&:hover": { background: flame[600] },
              "&.Mui-disabled": { background: delftBlue[100], color: delftBlue[400] },
            }}
          >
            Add company
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {selectedIds.length === 0 ? (
          <Typography sx={{ color: delftBlue[400], fontFamily: "Montserrat, sans-serif", fontSize: 14 }}>
            No companies assigned yet
          </Typography>
        ) : (
          <Stack spacing={1}>
            {selectedIds.map((companyId) => {
              const chip = versionChip(companyId);
              return (
                <Stack
                  key={companyId}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{
                    paddingX: 1.5,
                    paddingY: 1,
                    borderRadius: 1.5,
                    background: delftBlue[50],
                  }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: delftBlue[700],
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {companyNameById[companyId] || companyId}
                    </Typography>
                    <Chip
                      size="small"
                      label={chip.label}
                      sx={{
                        alignSelf: "flex-start",
                        height: 22,
                        fontSize: 11,
                        fontFamily: "Montserrat, sans-serif",
                        color: chip.color,
                        background: "white",
                      }}
                    />
                  </Stack>
                  <IconButton
                    aria-label={`Remove ${companyNameById[companyId] || "company"}`}
                    onClick={() => handleRemove(companyId)}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: "Audiowide, sans-serif", color: delftBlue[500] }}>
          Add company
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <TextField
              select
              fullWidth
              size="small"
              label="Company"
              value={companyToAdd}
              onChange={(event) => setCompanyToAdd(event.target.value)}
            >
              {availableToAdd.map((company) => (
                <MenuItem key={company.code} value={company.code}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            {hasTemplate ? (
              <TextField
                select
                fullWidth
                size="small"
                label="Form version"
                value={versionToAdd}
                onChange={(event) => setVersionToAdd(event.target.value)}
                helperText="This company will keep using this version until you publish another one."
              >
                {versions.map((row) => (
                  <MenuItem key={row.version} value={String(row.version)}>
                    {row.versionName ? `v${row.version} — ${row.versionName}` : `v${row.version}`}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Alert severity="info">
                No form template on this activity yet. The company will get activity access only.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            sx={{ textTransform: "none", background: flame[500], "&:hover": { background: flame[600] } }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityCompanyAssignments;
